import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Rate,
  Space,
  Switch,
  Typography,
} from "antd";
import toast from "react-hot-toast";
import { AlertCircle, Ban, CircleCheck, Eye, Loader2, PlayCircle, Plus, RotateCcw, Trash2, X } from "lucide-react";
import { Link, useParams } from "react-router-dom";

export type ExitLifecycleVariant = "employee" | "full";

export interface ExitLifecycleContentProps {
  exitId: string;
  variant: ExitLifecycleVariant;
  /** Breadcrumb links; disable when embedded in another screen */
  showHeaderLinks?: boolean;
  employeeName?: string;
  employeeCode?: string;
  exitStatusLabel?: string;
}
import {
  useApplyExitChecklistTemplateMutation,
  useApplyExitInterviewQuestionSetMutation,
  useCompleteExitChecklistTaskMutation,
  useCompleteExitLifecycleMutation,
  useCreateExitChecklistTaskMutation,
  useDeleteExitChecklistTaskMutation,
  useGetExitChecklistTemplatesQuery,
  useGetExitCompletionGatesQuery,
  useGetExitInterviewQuestionSetsQuery,
  useGetExitLifecycleQuery,
  useGetMyExitQuery,
  useMoveExitToFinalSettlementMutation,
  usePatchExitCompletionRequirementsMutation,
  usePatchExitInterviewSettingsMutation,
  usePatchExitInterviewResponsesMutation,
  usePutExitSettlementMutation,
  useCompleteExitSettlementMutation,
  useStartExitInProgressMutation,
  useSubmitExitInterviewMutation,
  useUpdateExitChecklistTaskMutation,
  useWaiveExitChecklistTaskMutation,
} from "../../../store/apis/exit.api";
import { useGetAllOrgUsersForFilterQuery } from "../../../store/apis/user.api";
import type {
  CreateExitChecklistTaskBody,
  ExitChecklistTaskDto,
  ExitExitInterviewDto,
  ExitInterviewQuestionDto,
  ExitSettlementDto,
  SettlementLineDto,
} from "../Utils/types";
import type { TableColumn } from "../../common/Table";
import { ExitChecklistDepartment } from "../Utils/types";
import {
  exitChecklistEditingAllowed,
  exitChecklistSequenceBlocksTask,
  exitInterviewQuestionSetApplyAllowed,
  exitInterviewRequired,
  exitInterviewResponsesEditingAllowed,
  exitSettlementEditingAllowed,
  exitSettlementRequired,
} from "../Utils/exitLifecycleUtils";
import { separationFormRequiredMark } from "../Components/separationFormRequiredMark";
import { separationTextAreaClassName } from "../Utils/separationTextAreaClassName";
import { SeparationStatusBadge } from "../Components/SeparationStatusBadge";
import { getErrorMessage } from "../../../store/utils/apiError";
import { useAuth } from "../../../store/hooks/useAuth";
import { useHasPermission } from "../../../store/hooks/useRbac";
import { PERMISSIONS } from "../../../utils/rbac/permissions";
import { useMyExitListQueryGate } from "../Utils/useMyExitListQueryGate";
import { ConfigurableTable, ConfirmationModal, Select } from "../../common";
import { SeparationAlert } from "../Components/SeparationAlert";
import { SeparationPageSkeleton, SeparationTableSkeleton, SeparationInterviewSkeleton } from "../Components/SeparationPageSkeleton";
import SimpleTooltip from "../../common/SimpleTooltip";

const checklistActionIconBtn =
  "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-800 disabled:opacity-40 disabled:cursor-not-allowed disabled:pointer-events-none";
const checklistActionIconBtnDanger =
  "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-red-600 transition-colors hover:bg-red-50 hover:text-red-700 disabled:opacity-40 disabled:cursor-not-allowed disabled:pointer-events-none";
const checklistActionIconBtnDisabled =
  "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-slate-400 cursor-not-allowed opacity-40 pointer-events-none";

const DEPT_OPTIONS = [
  { value: ExitChecklistDepartment.IT, label: "IT" },
  { value: ExitChecklistDepartment.ADMIN, label: "Admin" },
  { value: ExitChecklistDepartment.FINANCE, label: "Finance" },
  { value: ExitChecklistDepartment.HR, label: "HR" },
  { value: ExitChecklistDepartment.OTHER, label: "Other" },
];

const SETTLEMENT_LINE_TABLE_COLUMNS: TableColumn<SettlementLineDto>[] = [
  {
    key: "label",
    title: "Label",
    label: "Label",
    required: true,
    dataIndex: "label",
    truncate: false,
  },
  { key: "days", title: "Days", label: "Days", dataIndex: "days" },
  {
    key: "ratePerDay",
    title: "Rate/day",
    label: "Rate/day",
    dataIndex: "ratePerDay",
  },
  { key: "amount", title: "Amount", label: "Amount", dataIndex: "amount" },
];

const SETTLEMENT_SIMPLE_COLUMNS: TableColumn<SettlementLineDto>[] = [
  {
    key: "label",
    title: "Label",
    label: "Label",
    required: true,
    dataIndex: "label",
    truncate: false,
  },
  { key: "amount", title: "Amount", label: "Amount", dataIndex: "amount" },
];

function settlementLineRowKey(r: SettlementLineDto): string {
  if (r.id) return String(r.id);
  return [r.label, r.amount, r.days, r.ratePerDay]
    .map((v) => String(v ?? ""))
    .join("|");
}

function interviewQuestionKey(q: ExitInterviewQuestionDto): string {
  return String(q.questionKey ?? q.key ?? "");
}

function interviewQuestionLabel(q: ExitInterviewQuestionDto): string {
  const k = interviewQuestionKey(q);
  const label = q.prompt ?? q.label ?? k;
  return label ? String(label) : "Question";
}

function formatMultiSelectDisplay(val: string): string {
  try {
    const parsed = val ? (JSON.parse(val) as unknown) : [];
    if (Array.isArray(parsed)) return parsed.join(", ");
  } catch {
    /* ignore */
  }
  return val || "—";
}

function renderExitInterviewQuestionRow(
  q: ExitInterviewQuestionDto,
  editable: boolean,
  interviewDraft: Record<string, string>,
  setInterviewDraft: React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >,
): React.ReactNode {
  const k = interviewQuestionKey(q);
  if (!k) return null;
  const typ = String(q.type ?? "SHORT_TEXT");
  const val = interviewDraft[k] ?? "";
  const labelEl = (
    <div className="text-sm font-bold text-slate-700 mb-1">
      {interviewQuestionLabel(q)}
      {q.required ? (
        <span className="text-red-500 ml-0.5" aria-hidden>
          *
        </span>
      ) : null}
    </div>
  );

  let resolvedTyp = typ;
  const lowerLabel = String(q.prompt ?? q.label ?? "").toLowerCase();
  if (lowerLabel.includes("rating") || lowerLabel.includes("rate ") || lowerLabel.includes("rate our")) {
    if (typ === "SHORT_TEXT" || typ === "LONG_TEXT" || typ === "NUMBER") {
      resolvedTyp = "RATING_1_5";
    }
  }

  if (!editable) {
    let display: React.ReactNode = val || "—";
    if (resolvedTyp === "MULTI_SELECT") display = formatMultiSelectDisplay(val);
    if (resolvedTyp === "RATING_1_5" && val) display = `${val} / 5`;
    if (resolvedTyp === "BOOLEAN") {
      display =
        val === "true" ? "Yes" : val === "false" ? "No" : val ? String(val) : "—";
    }
    return (
      <div key={k}>
        {labelEl}
        <Typography.Text className="text-slate-600">{display}</Typography.Text>
      </div>
    );
  }

  if (resolvedTyp === "LONG_TEXT") {
    return (
      <div key={k}>
        {labelEl}
        <Input.TextArea
          className={separationTextAreaClassName}
          rows={3}
          value={val}
          onChange={(e) =>
            setInterviewDraft((m) => ({ ...m, [k]: e.target.value }))
          }
        />
      </div>
    );
  }
  if (resolvedTyp === "NUMBER") {
    return (
      <div key={k}>
        {labelEl}
        <InputNumber
          className="w-full"
          value={val ? Number(val) : undefined}
          min={1}
          onChange={(n) =>
            setInterviewDraft((m) => ({
              ...m,
              [k]: n === null || n === undefined ? "" : String(n),
            }))
          }
        />
      </div>
    );
  }
  if (resolvedTyp === "RATING_1_5") {
    const n = val ? Number(val) : 0;
    return (
      <div key={k}>
        {labelEl}
        <Radio.Group
          optionType="button"
          buttonStyle="solid"
          value={n >= 1 && n <= 5 ? n : undefined}
          onChange={(e) =>
            setInterviewDraft((m) => ({
              ...m,
              [k]: e.target.value ? String(e.target.value) : "",
            }))
          }
        >
          <Radio.Button value={1}>1</Radio.Button>
          <Radio.Button value={2}>2</Radio.Button>
          <Radio.Button value={3}>3</Radio.Button>
          <Radio.Button value={4}>4</Radio.Button>
          <Radio.Button value={5}>5</Radio.Button>
        </Radio.Group>
      </div>
    );
  }
  if (resolvedTyp === "BOOLEAN") {
    return (
      <div key={k}>
        {labelEl}
        <Radio.Group
          value={
            val === "true" ? "true" : val === "false" ? "false" : undefined
          }
          onChange={(e) =>
            setInterviewDraft((m) => ({
              ...m,
              [k]: String(e.target.value),
            }))
          }
        >
          <Radio value="true">Yes</Radio>
          <Radio value="false">No</Radio>
        </Radio.Group>
      </div>
    );
  }
  if (resolvedTyp === "SINGLE_SELECT" && Array.isArray(q.options)) {
    return (
      <div key={k}>
        {labelEl}
        <Select
          className="w-full"
          value={val || ""}
          clearable
          searchable
          options={q.options.map((o) => ({ value: o, label: o }))}
          onChange={(v) =>
            setInterviewDraft((m) => ({
              ...m,
              [k]: v === "" || v === undefined ? "" : String(v),
            }))
          }
        />
      </div>
    );
  }
  if (resolvedTyp === "MULTI_SELECT" && Array.isArray(q.options)) {
    let selected: string[] = [];
    try {
      selected = val ? (JSON.parse(val) as string[]) : [];
      if (!Array.isArray(selected)) selected = [];
    } catch {
      selected = [];
    }
    return (
      <div key={k}>
        {labelEl}
        <Select
          multiple
          className="w-full"
          searchable
          value={selected}
          options={q.options.map((o) => ({ value: o, label: o }))}
          onChange={(v) => {
            const arr = Array.isArray(v) ? v.map(String) : [];
            setInterviewDraft((m) => ({
              ...m,
              [k]: JSON.stringify(arr),
            }));
          }}
        />
      </div>
    );
  }
  return (
    <div key={k}>
      {labelEl}
      <Input
        value={val}
        onChange={(e) =>
          setInterviewDraft((m) => ({ ...m, [k]: e.target.value }))
        }
      />
    </div>
  );
}

export function ExitLifecycleContent({
  exitId,
  variant,
  showHeaderLinks = true,
  employeeName,
  employeeCode,
  exitStatusLabel,
}: ExitLifecycleContentProps) {
  const { user } = useAuth();
  const sessionReady = Boolean(user?.id);
  const isEmployee = variant === "employee";

  const hasMyExitView = useHasPermission(PERMISSIONS.MY_EXIT_VIEW);
  const hasMyExitManage = useHasPermission(PERMISSIONS.MY_EXIT_MANAGE);
  const hasMyExitPerm = hasMyExitView || hasMyExitManage;
  const hasResignationAccess =
    useHasPermission(PERMISSIONS.RESIGNATION_VIEW) ||
    useHasPermission(PERMISSIONS.RESIGNATION_MANAGE) ||
    useHasPermission(PERMISSIONS.RESIGNATION);
  const { skipMyExit } = useMyExitListQueryGate();
  const canHrView = useHasPermission(PERMISSIONS.EXIT_APPROVALS_VIEW);
  const canHrManage = useHasPermission(PERMISSIONS.EXIT_APPROVALS_MANAGE);
  const canExitApprovalsAct = useHasPermission(PERMISSIONS.EXIT_APPROVALS);
  const canSettlementView = useHasPermission(PERMISSIONS.EXIT_SETTLEMENT_VIEW);
  const canSettlementManage = useHasPermission(
    PERMISSIONS.EXIT_SETTLEMENT_MANAGE,
  );

  const { data: myExitList = [] } = useGetMyExitQuery(undefined, {
    skip: !sessionReady || skipMyExit,
  });
  const myExitIdSet = useMemo(
    () => new Set(myExitList.map((e) => String(e.id))),
    [myExitList],
  );

  /** Employee: exit id from GET my-exit, or resignation-only access (lifecycle GET enforces). */
  const mayViewLifecycle = Boolean(
    exitId &&
    (isEmployee
      ? myExitIdSet.has(String(exitId)) ||
      (!hasMyExitPerm && hasResignationAccess)
      : canHrView ||
      canHrManage ||
      canExitApprovalsAct ||
      canSettlementView ||
      canSettlementManage ||
      (hasMyExitPerm && myExitIdSet.has(String(exitId)))),
  );

  const showHrTools = canHrManage && !isEmployee;

  const {
    data: lifecycle,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetExitLifecycleQuery(exitId ?? "", {
    skip: !sessionReady || !exitId || !mayViewLifecycle,
  });

  const { data: gates } = useGetExitCompletionGatesQuery(exitId ?? "", {
    skip: !sessionReady || !exitId || !mayViewLifecycle,
  });

  const { data: templates = [] } = useGetExitChecklistTemplatesQuery(undefined, {
    skip: !sessionReady || !canHrManage || !exitId || isEmployee,
  });
  const { data: questionSets = [] } = useGetExitInterviewQuestionSetsQuery(
    undefined,
    { skip: !sessionReady || !canHrManage || !exitId || isEmployee },
  );
  const { data: orgUsers = [] } = useGetAllOrgUsersForFilterQuery(undefined, {
    /** Any user who can open this lifecycle may need id→name for checklist assignees. */
    skip: !sessionReady || !mayViewLifecycle,
  });

  const assigneeDisplay = useCallback(
    (t: ExitChecklistTaskDto) => {
      return t.assignedToName?.trim() || "—";
    },
    [],
  );

  const userOptions = useMemo(
    () =>
      orgUsers.map((u) => ({
        value: u.id,
        label: `${u.fullName}${u.empCode ? ` (${u.empCode})` : ""}`,
      })),
    [orgUsers],
  );

  const matchedUser = useMemo(() => {
    const empId = lifecycle?.employeeId;
    if (!empId) return null;
    return orgUsers.find((u) => String(u.id) === String(empId)) ?? null;
  }, [orgUsers, lifecycle?.employeeId]);

  const resolvedEmployeeName =
    employeeName ||
    matchedUser?.fullName ||
    (lifecycle?.employee as any)?.fullName;

  const resolvedEmployeeCode =
    employeeCode ||
    matchedUser?.empCode ||
    (lifecycle?.employee as any)?.employeeCode;

  const [patchReq, { isLoading: isPatchingReq }] =
    usePatchExitCompletionRequirementsMutation();
  const [startProgress, { isLoading: isStartingProgress }] =
    useStartExitInProgressMutation();
  const [moveFinal, { isLoading: isMovingFinal }] =
    useMoveExitToFinalSettlementMutation();
  const [completeExit, { isLoading: isCompletingExit }] =
    useCompleteExitLifecycleMutation();
  const [applyTpl, { isLoading: isApplyingTpl }] =
    useApplyExitChecklistTemplateMutation();
  const [addTask, { isLoading: isAddingTask }] =
    useCreateExitChecklistTaskMutation();
  const [patchTask, { isLoading: isPatchingTask }] =
    useUpdateExitChecklistTaskMutation();
  const [completeTaskMut, { isLoading: isCompletingTask }] =
    useCompleteExitChecklistTaskMutation();
  const [waiveTaskMut, { isLoading: isWaivingTask }] =
    useWaiveExitChecklistTaskMutation();
  const [delTask, { isLoading: isDeletingTask }] =
    useDeleteExitChecklistTaskMutation();
  const [applyQs, { isLoading: isApplyingQs }] =
    useApplyExitInterviewQuestionSetMutation();
  const [
    patchInterviewSettings,
    { isLoading: isPatchingInterviewSettings },
  ] = usePatchExitInterviewSettingsMutation();
  const [patchResponses, { isLoading: isPatchingInterview }] =
    usePatchExitInterviewResponsesMutation();
  const [submitInterview, { isLoading: isSubmittingInterview }] =
    useSubmitExitInterviewMutation();
  const [putSettlement, { isLoading: isSavingSettlement }] =
    usePutExitSettlementMutation();
  const [completeSettlement, { isLoading: isCompletingSettlement }] =
    useCompleteExitSettlementMutation();

  const [actionLoadingTaskId, setActionLoadingTaskId] = useState<string | null>(null);
  const [actionLoadingType, setActionLoadingType] = useState<string | null>(null);

  const isAnyChecklistActionLoading =
    isStartingProgress ||
    isMovingFinal ||
    isCompletingExit ||
    isPatchingTask ||
    isCompletingTask ||
    isWaivingTask ||
    isDeletingTask ||
    isApplyingTpl ||
    isAddingTask ||
    isPatchingReq ||
    !!actionLoadingTaskId;

  const shellClass = showHeaderLinks
    ? "p-4 max-w-6xl space-y-4"
    : "space-y-4 w-full min-w-0";

  const [applyTplOpen, setApplyTplOpen] = useState(false);
  const [tplId, setTplId] = useState<string | undefined>();
  const [addTaskOpen, setAddTaskOpen] = useState(false);
  const [addTaskForm] = Form.useForm<CreateExitChecklistTaskBody>();
  const [waiveOpen, setWaiveOpen] = useState(false);
  const [waiveTaskRow, setWaiveTaskRow] = useState<ExitChecklistTaskDto | null>(
    null,
  );
  const [waiveReason, setWaiveReason] = useState("");
  const [completeComments, setCompleteComments] = useState("");
  const [completeTaskRow, setCompleteTaskRow] =
    useState<ExitChecklistTaskDto | null>(null);
  const checklistTasks = useMemo(
    () => lifecycle?.checklistTasks ?? [],
    [lifecycle?.checklistTasks],
  );
  const totalTasks = checklistTasks.length;
  const completedTasks = useMemo(
    () =>
      checklistTasks.filter(
        (t) => t.status === "COMPLETED" || t.status === "WAIVED",
      ).length,
    [checklistTasks],
  );
  const [settlementOpen, setSettlementOpen] = useState(false);
  const [settlementForm] = Form.useForm<{
    notes?: string;
    refreshLeaveBalances?: boolean;
    encashmentLines?: SettlementLineDto[];
    additionLines?: SettlementLineDto[];
    deductionLines?: SettlementLineDto[];
  }>();
  const [viewTaskRow, setViewTaskRow] = useState<ExitChecklistTaskDto | null>(
    null,
  );
  const [interviewDraft, setInterviewDraft] = useState<
    Record<string, string>
  >({});
  const [qsId, setQsId] = useState<string | undefined>();
  const [checklistColumnPickerHost, setChecklistColumnPickerHost] =
    useState<HTMLDivElement | null>(null);
  const [interviewReqSaving, setInterviewReqSaving] = useState(false);
  const reqPatchKey: "interview" | "settlement" | null = interviewReqSaving
    ? "interview"
    : null;

  const [delTaskConfirmOpen, setDelTaskConfirmOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<ExitChecklistTaskDto | null>(
    null,
  );
  const [submitInterviewConfirmOpen, setSubmitInterviewConfirmOpen] =
    useState(false);
  const [completeSettlementConfirmOpen, setCompleteSettlementConfirmOpen] =
    useState(false);
  useEffect(() => {
    if (!lifecycle?.exitInterview) {
      setInterviewDraft({});
      return;
    }
    const iv = lifecycle.exitInterview as ExitExitInterviewDto;
    const map: Record<string, string> = {};
    for (const r of iv.responses ?? []) {
      const rk = r.questionKey || (r as any).key;
      if (rk) map[rk] = r.value ?? "";
    }
    setInterviewDraft(map);
  }, [lifecycle?.exitInterview, isEmployee]);

  const sortedInterviewQuestions = useMemo(() => {
    const iv = lifecycle?.exitInterview as ExitExitInterviewDto | null | undefined;
    const qs = iv?.questions ?? [];
    return [...qs].sort((a, b) => {
      const oa = a.order != null && !Number.isNaN(Number(a.order)) ? Number(a.order) : NaN;
      const ob = b.order != null && !Number.isNaN(Number(b.order)) ? Number(b.order) : NaN;
      const ha = !Number.isNaN(oa);
      const hb = !Number.isNaN(ob);
      if (ha && hb && oa !== ob) return oa - ob;
      if (ha && !hb) return -1;
      if (!ha && hb) return 1;
      return 0;
    });
  }, [lifecycle?.exitInterview]);

  const exitStatus = lifecycle?.exitStatus ?? "";
  const checklistEditable = exitChecklistEditingAllowed(exitStatus);
  const checklistWorkAllowed = [
    "EXIT_IN_PROGRESS",
    "FINAL_SETTLEMENT",
    "EXIT_COMPLETED",
  ].includes(exitStatus);
  const reqs = lifecycle?.completionRequirements ?? null;
  const reqInterview = exitInterviewRequired(reqs);
  const reqSettlement = exitSettlementRequired(reqs);
  const settlement = lifecycle?.exitSettlement as
    | ExitSettlementDto
    | null
    | undefined;

  useEffect(() => {
    if (settlementOpen && settlement) {
      settlementForm.setFieldsValue({
        notes: settlement.notes ?? "",
        encashmentLines: (settlement.encashmentLines ?? []) as any,
        additionLines: (settlement.additionLines ?? []) as any,
        deductionLines: (settlement.deductionLines ?? []) as any,
      });
    }
  }, [settlementOpen, settlement, settlementForm]);

  const canViewSettlementBlock =
    canSettlementView ||
    canSettlementManage ||
    showHrTools ||
    (isEmployee && settlement?.status === "COMPLETED");

  const canEditSettlement =
    (canSettlementManage || showHrTools) &&
    !isEmployee &&
    exitSettlementEditingAllowed(exitStatus);

  const preview = lifecycle?.completionGatesPreview ?? gates ?? null;
  const isAssignee = useCallback(
    (t: ExitChecklistTaskDto) => t.assignedToUserId === user?.id,
    [user?.id],
  );

  const canCompleteTaskForUser = useCallback(
    (t: ExitChecklistTaskDto) => {
      if (!checklistEditable || !checklistWorkAllowed) return false;
      if (t.status === "COMPLETED" || t.status === "WAIVED") return false;
      if (showHrTools) return true;
      return (
        isAssignee(t) &&
        (isEmployee ||
          hasMyExitView ||
          canHrView ||
          canExitApprovalsAct)
      );
    },
    [
      checklistEditable,
      checklistWorkAllowed,
      showHrTools,
      isAssignee,
      isEmployee,
      hasMyExitView,
      canHrView,
      canExitApprovalsAct,
    ],
  );

  const checklistColumns = useMemo<TableColumn<ExitChecklistTaskDto>[]>(
    () => [
      {
        key: "taskName",
        title: "Task",
        label: "Task",
        required: true,
        dataIndex: "taskName",
        truncate: true,
        width: 220,
        render: (name: unknown) => {
          const str = String(name ?? "—");
          return (
            <SimpleTooltip label={str} side="top" tooltipClassName="text-xs max-w-sm">
              <span className="font-medium text-slate-800 truncate block">
                {str}
              </span>
            </SimpleTooltip>
          );
        },
      },
      {
        key: "department",
        title: "Department",
        label: "Department",
        dataIndex: "department",
        width: 120,
      },
      {
        key: "assignee",
        title: "Assignee",
        label: "Assignee",
        width: 150,
        truncate: true,
        render: (_: unknown, t: ExitChecklistTaskDto) => assigneeDisplay(t),
      },
      {
        key: "status",
        title: "Approval Status",
        label: "Approval Status",
        dataIndex: "status",
        width: 140,
        render: (s: unknown) => {
          const raw = String(s ?? "");
          let label = raw || "—";
          if (raw === "PENDING") label = "Pending";
          else if (raw === "IN_PROGRESS") label = "In progress";
          else if (raw === "COMPLETED") label = "Completed";
          else if (raw === "WAIVED") label = "Waived";

          return (
            <SeparationStatusBadge
              label={label}
              phase={raw}
            />
          );
        },
      },
      {
        key: "actions",
        title: "Actions",
        label: "Actions",
        required: true,
        align: "center",
        width: 150,
        truncate: false,
        render: (_: unknown, t: ExitChecklistTaskDto) => {
          const seqBlock = exitChecklistSequenceBlocksTask(checklistTasks, t);
          const completeTooltip = seqBlock.blocked
            ? `Complete first: ${seqBlock.blockingTaskNames.join(", ")}`
            : "Mark task complete";

          const isTaskInProgressLoading =
            actionLoadingTaskId === t.id && actionLoadingType === "in_progress";
          const isTaskCompleteLoading =
            isCompletingTask && completeTaskRow?.id === t.id;
          const isTaskWaiveLoading =
            isWaivingTask && waiveTaskRow?.id === t.id;
          const isTaskDeleteLoading =
            isDeletingTask && taskToDelete?.id === t.id;

          return (
            <div className={`inline-flex flex-nowrap items-center justify-center gap-1 ${isAnyChecklistActionLoading ? "opacity-80" : ""}`}>
              <SimpleTooltip
                label="View task details"
                side="top"
                className="inline-flex shrink-0"
                tooltipClassName="text-xs shadow-md border-0"
              >
                <button
                  type="button"
                  aria-label="View task details"
                  onClick={() => setViewTaskRow(t)}
                  disabled={isAnyChecklistActionLoading}
                  className={
                    isAnyChecklistActionLoading
                      ? checklistActionIconBtnDisabled
                      : checklistActionIconBtn
                  }
                >
                  <Eye className="w-4 h-4 text-slate-600" aria-hidden />
                </button>
              </SimpleTooltip>
              {canCompleteTaskForUser(t) ? (
                <SimpleTooltip
                  label={completeTooltip}
                  side="top"
                  className="inline-flex shrink-0"
                  tooltipClassName="text-xs shadow-md border-0 max-w-xs"
                >
                  <span className="inline-flex">
                    <button
                      type="button"
                      disabled={seqBlock.blocked || isAnyChecklistActionLoading}
                      aria-label="Mark task complete"
                      onClick={() => {
                        if (seqBlock.blocked || isAnyChecklistActionLoading) return;
                        setCompleteTaskRow(t);
                        setCompleteComments("");
                      }}
                      className={
                        seqBlock.blocked || isAnyChecklistActionLoading
                          ? checklistActionIconBtnDisabled
                          : checklistActionIconBtn
                      }
                    >
                      {isTaskCompleteLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin text-emerald-600" aria-hidden />
                      ) : (
                        <CircleCheck
                          className="w-4 h-4 text-emerald-600"
                          aria-hidden
                        />
                      )}
                    </button>
                  </span>
                </SimpleTooltip>
              ) : null}
              {showHrTools && checklistEditable ? (
                <>
                  {checklistWorkAllowed && (
                    <>
                      <SimpleTooltip
                        label="Set status to in progress"
                        side="top"
                        className="inline-flex shrink-0"
                        tooltipClassName="text-xs shadow-md border-0"
                      >
                        <button
                          type="button"
                          aria-label="Set status to in progress"
                          onClick={async () => {
                            if (isAnyChecklistActionLoading) return;
                            setActionLoadingTaskId(t.id);
                            setActionLoadingType("in_progress");
                            try {
                              await patchTask({
                                exitId,
                                taskId: t.id,
                                body: { status: "IN_PROGRESS" },
                              }).unwrap();
                              toast.success("Task status updated successfully");
                            } catch (e) {
                              toast.error(
                                getErrorMessage(e as never) ?? "Action could not be completed",
                              );
                            } finally {
                              setActionLoadingTaskId(null);
                              setActionLoadingType(null);
                            }
                          }}
                          className={
                            isAnyChecklistActionLoading
                              ? checklistActionIconBtnDisabled
                              : checklistActionIconBtn
                          }
                          disabled={isAnyChecklistActionLoading}
                        >
                          {isTaskInProgressLoading ? (
                            <Loader2 className="w-4 h-4 animate-spin text-blue-600" aria-hidden />
                          ) : (
                            <PlayCircle
                              className="w-4 h-4 text-blue-600"
                              aria-hidden
                            />
                          )}
                        </button>
                      </SimpleTooltip>
                      <SimpleTooltip
                        label="Waive task"
                        side="top"
                        className="inline-flex shrink-0"
                        tooltipClassName="text-xs shadow-md border-0"
                      >
                        <button
                          type="button"
                          aria-label="Waive task"
                          onClick={() => {
                            if (isAnyChecklistActionLoading) return;
                            setWaiveTaskRow(t);
                            setWaiveReason("");
                            setWaiveOpen(true);
                          }}
                          disabled={isAnyChecklistActionLoading}
                          className={
                            isAnyChecklistActionLoading
                              ? checklistActionIconBtnDisabled
                              : checklistActionIconBtn
                          }
                        >
                          {isTaskWaiveLoading ? (
                            <Loader2 className="w-4 h-4 animate-spin text-amber-600" aria-hidden />
                          ) : (
                            <Ban className="w-4 h-4 text-amber-600" aria-hidden />
                          )}
                        </button>
                      </SimpleTooltip>
                    </>
                  )}
                  <SimpleTooltip
                    label="Remove task"
                    side="top"
                    className="inline-flex shrink-0"
                    tooltipClassName="text-xs shadow-md border-0"
                  >
                    <button
                      type="button"
                      aria-label="Remove task"
                      onClick={() => {
                        if (isAnyChecklistActionLoading) return;
                        setTaskToDelete(t);
                        setDelTaskConfirmOpen(true);
                      }}
                      disabled={isAnyChecklistActionLoading}
                      className={
                        isAnyChecklistActionLoading
                          ? checklistActionIconBtnDisabled
                          : checklistActionIconBtnDanger
                      }
                    >
                      {isTaskDeleteLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin text-red-600" aria-hidden />
                      ) : (
                        <Trash2 className="w-4 h-4 text-red-600" aria-hidden />
                      )}
                    </button>
                  </SimpleTooltip>
                </>
              ) : null}
            </div>
          );
        },
      },
    ],
    [
      checklistTasks,
      assigneeDisplay,
      checklistEditable,
      checklistWorkAllowed,
      showHrTools,
      isEmployee,
      hasMyExitView,
      canHrView,
      canExitApprovalsAct,
      user?.id,
      exitId,
      patchTask,
      delTask,
      canCompleteTaskForUser,
      isAnyChecklistActionLoading,
      actionLoadingTaskId,
      actionLoadingType,
      isCompletingTask,
      completeTaskRow,
      isWaivingTask,
      waiveTaskRow,
      isDeletingTask,
      taskToDelete,
    ],
  );

  const runApplyTemplate = async () => {
    if (!exitId || !tplId) return;
    try {
      await applyTpl({ exitId, templateId: tplId }).unwrap();
      toast.success("Checklist template applied successfully");
      setApplyTplOpen(false);
      setTplId(undefined);
    } catch (e) {
      toast.error(getErrorMessage(e as never) ?? "Could not apply checklist template");
    }
  };

  const submitAddTask = async () => {
    if (!exitId) return;
    try {
      const v = await addTaskForm.validateFields();
      await addTask({
        exitId,
        body: {
          taskName: v.taskName.trim(),
          department: v.department,
          assignedToUserId: v.assignedToUserId,
          isMandatory: Boolean(v.isMandatory),
        },
      }).unwrap();
      toast.success("Checklist task added successfully");
      setAddTaskOpen(false);
      addTaskForm.resetFields();
    } catch (e) {
      if (e && typeof e === "object" && "errorFields" in e) return;
      toast.error(getErrorMessage(e as never) ?? "Failed");
    }
  };

  const submitWaive = async () => {
    if (!exitId || !waiveTaskRow || !waiveReason.trim()) {
      toast.error("A reason is required to waive this task");
      return;
    }
    try {
      await waiveTaskMut({
        exitId,
        taskId: waiveTaskRow.id,
        reason: waiveReason.trim(),
      }).unwrap();
      toast.success("Checklist task waived successfully");
      setWaiveOpen(false);
      setWaiveTaskRow(null);
      setWaiveReason("");
    } catch (e) {
      toast.error(getErrorMessage(e as never) ?? "Could not waive the checklist task");
    }
  };

  const submitCompleteTask = async () => {
    if (!exitId || !completeTaskRow) return;
    try {
      await completeTaskMut({
        exitId,
        taskId: completeTaskRow.id,
        comments: completeComments.trim() || undefined,
      }).unwrap();
      toast.success("Checklist task marked as completed");
      setCompleteTaskRow(null);
      setCompleteComments("");
    } catch (e) {
      toast.error(getErrorMessage(e as never) ?? "Could not mark task as completed");
    }
  };

  const saveInterviewDraft = async () => {
    if (!exitId) return;
    const responses = Object.entries(interviewDraft).map(([questionKey, value]) => ({
      questionKey,
      value,
    }));
    try {
      await patchResponses({ exitId, responses }).unwrap();
      toast.success("Exit interview draft saved successfully");
    } catch (e) {
      toast.error(getErrorMessage(e as never) ?? "Could not save interview draft");
    }
  };

  const interviewRequiredFieldMessage = (): string | null => {
    for (const q of sortedInterviewQuestions) {
      if (!q.required) continue;
      const k = interviewQuestionKey(q);
      if (!k) continue;
      const v = interviewDraft[k] ?? "";
      const typ = String(q.type ?? "SHORT_TEXT");
      if (typ === "MULTI_SELECT") {
        try {
          const arr = v ? (JSON.parse(v) as unknown) : [];
          if (!Array.isArray(arr) || arr.length === 0) {
            return `Please complete: ${interviewQuestionLabel(q)}`;
          }
        } catch {
          return `Please complete: ${interviewQuestionLabel(q)}`;
        }
      } else if (typ === "RATING_1_5") {
        const n = Number(v);
        if (!v.trim() || Number.isNaN(n) || n < 1 || n > 5) {
          return `Please complete: ${interviewQuestionLabel(q)}`;
        }
      } else if (typ === "BOOLEAN") {
        if (
          q.required &&
          v !== "true" &&
          v !== "false"
        ) {
          return `Please complete: ${interviewQuestionLabel(q)}`;
        }
      } else if (!String(v).trim()) {
        return `Please complete: ${interviewQuestionLabel(q)}`;
      }
    }
    return null;
  };

  const submitInterviewWithValidation = async () => {
    if (!exitId) return;
    const msg = interviewRequiredFieldMessage();
    if (msg) {
      toast.error(msg);
      return;
    }

    setSubmitInterviewConfirmOpen(true);
  };

  const submitSettlement = async () => {
    if (!exitId) return;
    try {
      const v = await settlementForm.validateFields();
      const trimLines = (lines: SettlementLineDto[] | undefined) =>
        (lines ?? []).filter(
          (row) =>
            row.label ||
            row.description ||
            row.amount != null ||
            row.days != null ||
            row.ratePerDay != null,
        );
      await putSettlement({
        exitId,
        body: {
          notes: v.notes,
          refreshLeaveBalances: Boolean(v.refreshLeaveBalances),
          encashmentLines: trimLines(v.encashmentLines).map((l) => ({
            leaveTypeId: l.leaveTypeId,
            leaveTypeName: l.leaveTypeName,
            days: l.days,
            ratePerDay: l.ratePerDay,
            amount: l.amount,
          })),
          additionLines: trimLines(v.additionLines).map((l) => ({
            label: (l.label || l.description || "").trim(),
            amount: l.amount ?? 0,
          })),
          deductionLines: trimLines(v.deductionLines).map((l) => ({
            label: (l.label || l.description || "").trim(),
            amount: l.amount ?? 0,
          })),
        },
      }).unwrap();
      toast.success("Exit settlement details saved successfully");
      setSettlementOpen(false);
    } catch (e) {
      if (e && typeof e === "object" && "errorFields" in e) return;
      toast.error(getErrorMessage(e as never) ?? "Could not save settlement details");
    }
  };

  if (!mayViewLifecycle) {
    return (
      <div className="p-4 max-w-lg space-y-2">
        <Alert
          type="warning"
          showIcon
          message={
            isEmployee
              ? "We could not match this exit to your current requests. Refresh the page, or confirm your resignation appears under “Your resignation request(s)” on the Resignation tab."
              : "You cannot open this exit lifecycle."
          }
        />
        <Link to="/separation/resign">Back to resignation</Link>
      </div>
    );
  }

  if (!sessionReady || isLoading || isFetching) {
    return showHeaderLinks ? (
      <div className={shellClass}>
        <SeparationPageSkeleton withInterview />
      </div>
    ) : (
      <div className="space-y-6">
        <SeparationTableSkeleton rows={6} />
        <SeparationInterviewSkeleton />
      </div>
    );
  }

  if (isError || !lifecycle) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[65vh] p-6 text-center w-full grow">
        <div className="w-20 h-20 bg-slate-50 text-slate-400 rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm border border-slate-100/80">
          <AlertCircle size={40} strokeWidth={1.5} />
        </div>
        <h2 className="text-3xl font-bold text-slate-900 mb-3 tracking-tight">Could not load exit lifecycle</h2>
        <p className="text-slate-500 mb-8 text-lg max-w-lg mx-auto leading-relaxed">
          We encountered an issue while retrieving the separation details. This could be due to a temporary connection problem or a missing record.
        </p>
        <Button
          type="primary"
          onClick={() => void refetch()}
          className="mx-auto h-12 rounded-xl flex items-center justify-center gap-2 px-10 font-medium shadow-md hover:shadow-lg transition-all"
        >
          <RotateCcw size={20} />
          Retry
        </Button>
      </div>
    );
  }

  const interview = lifecycle.exitInterview as ExitExitInterviewDto | null | undefined;
  const lockedInterview = Boolean(interview?.locked || interview?.isSubmitted);
  const employeeSelfSubmitEnabled = Boolean(interview?.employeeSelfSubmitEnabled);
  const interviewResponsePhaseOk =
    exitInterviewResponsesEditingAllowed(exitStatus);
  const canApplyInterviewSet =
    showHrTools && !lockedInterview && exitInterviewQuestionSetApplyAllowed(exitStatus);
  const canHrInterviewEdit =
    showHrTools && !lockedInterview && interviewResponsePhaseOk;
  const canEmployeeEditInterview =
    isEmployee &&
    employeeSelfSubmitEnabled &&
    !lockedInterview &&
    interviewResponsePhaseOk;
  const alertsBlock = (
    ["SUBMITTED", "MANAGER_APPROVAL", "MANAGER_APPROVED", "HR_REVIEW"].includes(
      exitStatus,
    ) || exitStatus === "EXIT_COMPLETED" || exitStatus === "REJECTED" || exitStatus === "WITHDRAWN" ? (
      <div className="space-y-3">
        {["SUBMITTED", "MANAGER_APPROVAL", "MANAGER_APPROVED", "HR_REVIEW"].includes(
          exitStatus,
        ) ? (
          <SeparationAlert
            type="info"
            title="Approval Required"
            description="This exit request is currently pending approval. Lifecycle actions (Notice Period, Settlement, etc.) will unlock once the resignation is approved by the required parties."
          />
        ) : null}
        {exitStatus === "EXIT_COMPLETED" ? (
          <SeparationAlert
            type="success"
            title="Exit Process Completed"
            description="The separation process for this employee has been finalized and completed. All checklist items, interviews, and settlements are now locked."
          />
        ) : null}
        {exitStatus === "REJECTED" ? (
          <SeparationAlert
            type="error"
            title="Exit Request Rejected"
            description="This exit request was rejected. The process has been terminated."
          />
        ) : null}
        {exitStatus === "WITHDRAWN" ? (
          <SeparationAlert
            type="warning"
            title="Exit Request Withdrawn"
            description="This exit request has been withdrawn by the employee."
          />
        ) : null}
      </div>
    ) : null
  );

  return (
    <div className={shellClass}>
      {((resolvedEmployeeName && !isEmployee) || showHeaderLinks) ? (
        <div className="flex flex-wrap gap-3 justify-between items-start mb-2">
          <div className="w-full">
            {resolvedEmployeeName && !isEmployee ? (
              <div className="flex justify-between items-center gap-2 mb-6 w-full">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-slate-500 inline-block w-[115px] shrink-0">
                      Employee Name:
                    </span>
                    <span className="text-lg font-semibold text-slate-900">
                      {resolvedEmployeeName}
                    </span>
                  </div>
                  {resolvedEmployeeCode ? (
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-slate-500 inline-block w-[92px] shrink-0">
                        Employee ID:
                      </span>
                      <span className="text-sm font-semibold text-slate-700">
                        {resolvedEmployeeCode}
                      </span>
                    </div>
                  ) : null}
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <span className="text-xs font-semibold text-slate-500 min-w-[120px]">
                    Request status:
                  </span>
                  <SeparationStatusBadge
                    label={
                      exitStatusLabel ||
                      (exitStatus ? exitStatus.replace(/_/g, " ") : "—")
                    }
                    phase={exitStatus}
                  />
                </div>
              </div>
            ) : null}

            {showHeaderLinks ? (
              <Space wrap>
                <Link
                  to="/separation/resign"
                  className="text-primary-600 text-sm"
                >
                  Resignation
                </Link>
                {exitId ? (
                  <>
                    <span className="text-slate-300">/</span>
                    <span className="text-slate-600 text-sm font-medium">
                      Lifecycle
                    </span>
                  </>
                ) : null}
              </Space>
            ) : null}
          </div>
        </div>
      ) : null}

      {!isEmployee ? alertsBlock : null}


      {/* <h2 className="text-lg font-semibold text-slate-800 mb-2">
            {isEmployee
              ? "Exit checklist & interview"
              : "Exit checklist & lifecycle"}
          </h2> */}
      {/* <p className="text-sm text-slate-500">
            Exit <Typography.Text code>{lifecycle.exitId}</Typography.Text>
            {lifecycle.employeeId ? (
              <>
                {" "}
                · Employee{" "}
                <Typography.Text code>{lifecycle.employeeId}</Typography.Text>
              </>
            ) : null}
          </p> */}



      {/* {preview ? (
        <Alert
          type={preview.allSatisfied ? "success" : "info"}
          showIcon
          message={
            <span>
              Completion gates: checklist{" "}
              {preview.mandatoryChecklistSatisfied ? "OK" : "pending"}
              {reqInterview
                ? ` · interview ${preview.interviewSatisfied ? "OK" : "pending"}`
                : ""}
              {reqSettlement
                ? ` · settlement ${preview.settlementSatisfied ? "OK" : "pending"}`
                : ""}
            </span>
          }
          description={
            preview.allSatisfied
              ? "All configured gates are satisfied. You can complete the exit when status allows."
              : "Complete exit stays disabled until all required gates pass."
          }
        />
      ) : null} */}

      {showHrTools ? (
        <Card
          title={<span className="text-base font-semibold text-slate-900">Lifecycle Actions (HR)</span>}
          className="border-slate-300/50"
          styles={{ header: { padding: '12px 16px', minHeight: 'auto' }, body: { padding: '24px 20px' } }}
        >
          <Space wrap>
            <Button
              disabled={
                exitStatus !== "NOTICE_PERIOD" || isAnyChecklistActionLoading
              }
              loading={isStartingProgress}
              onClick={async () => {
                try {
                  await startProgress(exitId).unwrap();
                  toast.success("Exit process moved to in-progress status");
                } catch (e) {
                  toast.error(getErrorMessage(e as never) ?? "Process status update failed");
                }
              }}
            >
              Start in progress
            </Button>
            <Button
              disabled={
                exitStatus !== "EXIT_IN_PROGRESS" || isAnyChecklistActionLoading
              }
              loading={isMovingFinal}
              onClick={async () => {
                try {
                  await moveFinal(exitId).unwrap();
                  toast.success("Exit process moved to final settlement");
                } catch (e) {
                  toast.error(getErrorMessage(e as never) ?? "Status transition could not be completed");
                }
              }}
            >
              Move to final settlement
            </Button>
            <Button
              type={preview?.allSatisfied ? "primary" : "default"}
              disabled={
                exitStatus !== "FINAL_SETTLEMENT" || isAnyChecklistActionLoading
              }
              loading={isCompletingExit}
              onClick={async () => {
                if (!preview?.allSatisfied) {
                  if (reqSettlement && !preview?.settlementSatisfied) {
                    toast.error("Please mark settlement complete before finalizing");
                  } else if (reqInterview && !preview?.interviewSatisfied) {
                    toast.error(
                      "Please complete the exit interview before finalizing",
                    );
                  } else if (!preview?.mandatoryChecklistSatisfied) {
                    toast.error(
                      "Please complete all mandatory checklist tasks before finalizing",
                    );
                  } else {
                    toast.error(
                      "Please complete all required gates before finalizing",
                    );
                  }
                  return;
                }
                try {
                  await completeExit(exitId).unwrap();
                  toast.success(
                    "The exit process has been completed successfully",
                  );
                } catch (e) {
                  toast.error(
                    getErrorMessage(e as never) ??
                    "Process completion could not be finalized",
                  );
                }
              }}
            >
              Complete exit
            </Button>
          </Space>
        </Card>
      ) : null}

      <div
        className={
          isEmployee
            ? "bg-white rounded-xl shadow-soft border border-slate-300/50 p-6 space-y-6 w-full min-w-0"
            : "space-y-6 w-full min-w-0"
        }
      >
        {isEmployee ? alertsBlock : null}

        <div className="bg-white rounded-xl shadow-soft border border-slate-300/50 overflow-hidden w-full min-w-0">
          <div className="pl-4 sm:pl-6 pr-4 py-3 border-b border-slate-300/50 flex flex-wrap items-center justify-between gap-3 bg-white">
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold text-slate-900 m-0">Checklist</h3>
              {totalTasks > 0 && (
                <span className="text-xs font-normal text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                  {completedTasks}/{totalTasks} Completed
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center justify-end gap-4">
              {showHrTools && checklistEditable ? (
                <div className="flex flex-wrap items-center gap-3">

                  <Space size={12}>
                    <Button
                      className="hover:border-primary-500 hover:text-primary-600"
                      onClick={() => setApplyTplOpen(true)}
                    >
                      Apply template
                    </Button>
                    <Button
                      type="primary"
                      className="shadow-sm"
                      onClick={() => {
                        addTaskForm.resetFields();
                        addTaskForm.setFieldsValue({
                          department: ExitChecklistDepartment.IT,
                          isMandatory: true,
                        });
                        setAddTaskOpen(true);
                      }}
                    >
                      Add task
                    </Button>
                  </Space>
                </div>
              ) : null}
              <div
                ref={setChecklistColumnPickerHost}
                className="shrink-0 flex items-center min-h-[36px]"
              />
            </div>
          </div>

          <ConfigurableTable<ExitChecklistTaskDto>
            columns={checklistColumns}
            data={checklistTasks}
            rowKey="id"
            emptyMessage="No checklist tasks."
            bordered={false}
            hoverable
            size="sm"
            spacing={0}
            className="min-w-0 [&_thead_th]:!text-slate-500 [&_th:first-child]:!pl-4 [&_th:first-child]:sm:!pl-6 [&_td:first-child]:!pl-4 [&_td:first-child]:sm:!pl-6 [&_th:last-child]:!pr-4 [&_th:last-child]:sm:!pr-6 [&_td:last-child]:!pr-4 [&_td:last-child]:sm:!pr-6"
            columnPickerPortal
            columnPortalHost={checklistColumnPickerHost}
            configOptions={{
              persistenceKey: `separation-exit-checklist-${variant}`,
            }}
          />
        </div>

        <Card
          title={<span className="text-base font-semibold text-slate-900">Exit Interview</span>}
          className="border-slate-300/50 w-full min-w-0"
          styles={{ header: { padding: '12px 16px', minHeight: 'auto' }, body: { padding: '16px 20px' } }}
        >
          {canApplyInterviewSet || (canHrInterviewEdit && interview && (interview.questions?.length ?? 0) > 0) ? (
            <div className="flex flex-wrap items-center gap-6 mb-4">
              {canApplyInterviewSet ? (
                <div className="flex items-center gap-2">
                  <Select
                    placeholder="Question set"
                    className="min-w-[180px] sm:min-w-[200px]"
                    clearable
                    searchable
                    value={qsId ?? ""}
                    onChange={(v) => setQsId(v ? String(v) : undefined)}
                    options={questionSets.map((q) => ({
                      value: q.id,
                      label: q.name ?? q.id,
                    }))}
                  />
                  <Button
                    disabled={!qsId || isApplyingQs}
                    loading={isApplyingQs}
                    onClick={async () => {
                      if (!qsId) return;
                      try {
                        await applyQs({ exitId, questionSetId: qsId }).unwrap();
                        toast.success("Exit interview question set applied successfully");
                        setQsId(undefined);
                      } catch (e) {
                        toast.error(getErrorMessage(e as never) ?? "The question set could not be applied");
                      }
                    }}
                  >
                    Apply question set
                  </Button>
                </div>
              ) : null}
              {canHrInterviewEdit && interview && (interview.questions?.length ?? 0) > 0 ? (
                <div className="flex items-center gap-2.5">
                  <span className="text-sm font-medium text-slate-700">
                    Employee self-submit
                  </span>
                  <Switch
                    checked={employeeSelfSubmitEnabled}
                    loading={isPatchingInterviewSettings}
                    onChange={async (checked) => {
                      try {
                        await patchInterviewSettings({
                          exitId,
                          body: { employeeSelfSubmitEnabled: checked },
                        }).unwrap();
                        toast.success(
                          checked
                            ? "Employees can now fill and submit this interview"
                            : "Employee self-submit turned off",
                        );
                      } catch (e) {
                        toast.error(
                          getErrorMessage(e as never) ?? "Could not update",
                        );
                      }
                    }}
                  />
                </div>
              ) : null}
            </div>
          ) : null}
          {!interview || !(interview.questions?.length) ? (
            <Typography.Text type="secondary" className="px-2">
              No interview configured for this exit.
            </Typography.Text>
          ) : (
            <>
              {lockedInterview && (
                <SeparationAlert
                  type="info"
                  title="Interview submitted"
                  description="This interview is locked; answers can no longer be changed."
                  className="mb-4"
                />
              )}
              <div
                className={
                  isEmployee
                    ? "space-y-4 w-full min-w-0"
                    : "space-y-4 max-w-2xl"
                }
              >
                <div className="space-y-3">
                  {sortedInterviewQuestions.map((q) =>
                    renderExitInterviewQuestionRow(
                      q,
                      canEmployeeEditInterview || canHrInterviewEdit,
                      interviewDraft,
                      setInterviewDraft,
                    ),
                  )}
                </div>
                {canEmployeeEditInterview || canHrInterviewEdit ? (
                  <div className="flex items-center gap-4">
                    <Space wrap>
                      <Button
                        // loading={isPatchingInterview}
                        disabled={isSubmittingInterview || isPatchingInterview}
                        onClick={() => void saveInterviewDraft()}
                      >
                        Save draft
                      </Button>
                      <Button
                        type="primary"
                        loading={isSubmittingInterview}
                        disabled={isSubmittingInterview || isPatchingInterview}
                        onClick={() => void submitInterviewWithValidation()}
                      >
                        Submit interview
                      </Button>
                    </Space>
                    {(isPatchingInterview || isSubmittingInterview) && (
                      <Typography.Text type="secondary" className="animate-pulse flex items-center gap-2">
                        <div className="w-1 h-1 rounded-full bg-slate-400 animate-bounce" />
                        Saving changes...
                      </Typography.Text>
                    )}
                  </div>
                ) : isEmployee ? (
                  <div className="mt-6 pt-4 border-t border-slate-100">
                    <Typography.Text type="secondary" className="text-xs">
                      * Your exit interview is completed by HR unless they enable self-submit for you.
                    </Typography.Text>
                  </div>
                ) : (
                  <div className="mt-6 pt-4 border-t border-slate-100">
                    <Typography.Text type="secondary" className="text-xs">
                      * Interview answers cannot be edited in the current exit phase.
                    </Typography.Text>
                  </div>
                )}
              </div>
            </>
          )}
        </Card>
      </div>

      {showHrTools ? (
        <Card
          title={<span className="text-base font-semibold text-slate-900">Completion Requirements (HR)</span>}
          className="border-slate-300/50"
          styles={{ header: { padding: '12px 16px', minHeight: 'auto' }, body: { padding: "20px 16px" } }}
        >
          <div className="flex flex-wrap items-center gap-6 sm:gap-8">
            <div className="flex items-center gap-2.5">
              <span className="text-sm font-medium text-slate-700">
                Require interview submitted before completion
              </span>
              <Switch
                checked={reqInterview}
                loading={!!reqPatchKey}
                onChange={async (checked) => {
                  setInterviewReqSaving(true);
                  try {
                    await patchReq({
                      exitId,
                      body: { requireInterviewSubmitted: checked },
                    }).unwrap();
                    toast.success("Completion requirements updated successfully");
                  } catch (e) {
                    toast.error(getErrorMessage(e as never) ?? "Could not update completion requirements");
                  } finally {
                    setInterviewReqSaving(false);
                  }
                }}
              />
            </div>
            <div className="flex items-center gap-2.5">
              <span className="text-sm font-medium text-slate-700">
                Require settlement done before completion
              </span>
              <Switch
                checked={reqSettlement}
                loading={!!reqPatchKey}
                onChange={async (checked) => {
                  setInterviewReqSaving(true);
                  try {
                    await patchReq({
                      exitId,
                      body: { requireSettlementCompleted: checked },
                    }).unwrap();
                    toast.success("Completion requirements updated successfully");
                  } catch (e) {
                    toast.error(getErrorMessage(e as never) ?? "Could not update completion requirements");
                  } finally {
                    setInterviewReqSaving(false);
                  }
                }}
              />
            </div>
          </div>
        </Card>
      ) : null}


      {canViewSettlementBlock ? (
        <Card
          title={<span className="text-base font-semibold text-slate-900">Settlement</span>}
          className="border-slate-300/50"
          styles={{ header: { padding: '12px 16px', minHeight: 'auto' }, body: { padding: 0 } }}
          extra={
            canEditSettlement && settlement?.status !== "COMPLETED" ? (
              <Space>
                <Button
                  className="hover:border-primary-500 hover:text-primary-600"
                  onClick={() => setSettlementOpen(true)}
                >
                  Edit settlement
                </Button>
                <SimpleTooltip
                  label={
                    exitStatus !== "FINAL_SETTLEMENT"
                      ? "Move the process to 'Move to final settlement' under Lifecycle Actions (HR) first"
                      : undefined
                  }
                  side="top"
                >
                  <span>
                    <Button
                      type="primary"
                      className="shadow-sm"
                      disabled={
                        exitStatus !== "FINAL_SETTLEMENT" ||
                        isCompletingSettlement ||
                        isAnyChecklistActionLoading
                      }
                      loading={isCompletingSettlement}
                      onClick={() => {
                        if (exitStatus !== "FINAL_SETTLEMENT") return;
                        setCompleteSettlementConfirmOpen(true);
                      }}
                    >
                      Mark settlement complete
                    </Button>
                  </span>
                </SimpleTooltip>
              </Space>
            ) : null
          }
        >
          {!settlement ? (
            <Typography.Text type="secondary" className="px-6 py-6 block">
              No settlement data (employee view may be redacted until
              completed).
            </Typography.Text>
          ) : (
            <div className="space-y-6 p-4 sm:p-6">
              <div className="max-w-md">
                <div className="flex items-center gap-4 mb-4">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider min-w-[100px]">
                    Status:
                  </span>
                  <span className="text-sm font-semibold text-slate-700 bg-slate-100 px-2.5 py-1 rounded-md">
                    {settlement.status ?? "—"}
                  </span>
                </div>
                {settlement.netPayable != null ? (
                  <div className="flex items-center gap-4">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider min-w-[100px]">
                      Net payable:
                    </span>
                    <span className="text-lg font-bold text-slate-900">
                      {settlement.netPayable}
                    </span>
                  </div>
                ) : null}
              </div>

              {settlement.notes ? (
                <div>
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                      Notes
                    </div>
                    <div className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">
                      {settlement.notes}
                    </div>
                  </div>
                </div>
              ) : null}

              <div className="space-y-6 pt-2">
                <div>
                  <h5 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">
                    Encashment lines
                  </h5>
                  <ConfigurableTable<SettlementLineDto>
                    className="w-full min-w-0"
                    data={settlement.encashmentLines ?? []}
                    columns={SETTLEMENT_LINE_TABLE_COLUMNS}
                    rowKey={(r) => `enc-${settlementLineRowKey(r)}`}
                    bordered={true}
                    hoverable
                    size="sm"
                    emptyMessage="No encashment lines."
                    spacing={0}
                    hideColumnPicker={true}
                  />
                </div>

                <div>
                  <h5 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">
                    Additions
                  </h5>
                  <ConfigurableTable<SettlementLineDto>
                    className="w-full min-w-0"
                    data={settlement.additionLines ?? []}
                    columns={SETTLEMENT_SIMPLE_COLUMNS}
                    rowKey={(r) => `add-${settlementLineRowKey(r)}`}
                    bordered={true}
                    hoverable
                    size="sm"
                    emptyMessage="No addition lines."
                    spacing={0}
                    hideColumnPicker={true}
                  />
                </div>

                <div>
                  <h5 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">
                    Deductions
                  </h5>
                  <ConfigurableTable<SettlementLineDto>
                    className="w-full min-w-0"
                    data={settlement.deductionLines ?? []}
                    columns={SETTLEMENT_SIMPLE_COLUMNS}
                    rowKey={(r) => `ded-${settlementLineRowKey(r)}`}
                    bordered={true}
                    hoverable
                    size="sm"
                    emptyMessage="No deduction lines."
                    spacing={0}
                    hideColumnPicker={true}
                  />
                </div>
              </div>
            </div>
          )}
        </Card>
      ) : null}

      <Modal
        centered
        transitionName=""
        maskTransitionName=""
        title="Apply checklist template"
        open={applyTplOpen}
        confirmLoading={isApplyingTpl}
        onCancel={() => setApplyTplOpen(false)}
        onOk={() => void runApplyTemplate()}
        okText="Apply"
      >
        <Select
          className="w-full"
          placeholder="Select template"
          searchable
          value={tplId ?? ""}
          onChange={(v) => setTplId(v ? String(v) : undefined)}
          options={templates.map((t) => ({
            value: t.id,
            label: `${t.name} (${t.department})`,
          }))}
          clearable
        />
      </Modal>

      <Modal
        centered
        transitionName=""
        maskTransitionName=""
        title="Add checklist task"
        open={addTaskOpen}
        confirmLoading={isAddingTask}
        onCancel={() => setAddTaskOpen(false)}
        onOk={() => void submitAddTask()}
        destroyOnClose
      >
        <Form
          form={addTaskForm}
          layout="vertical"
          requiredMark={separationFormRequiredMark}
        >
          <Form.Item
            name="taskName"
            label="Task name"
            rules={[{ required: true, message: "Task name is required" }]}
          >
            <Input placeholder="Enter task name" />
          </Form.Item>
          <Form.Item
            name="department"
            label="Department"
            rules={[{ required: true, message: "Department is required" }]}
            getValueFromEvent={(v) => v}
          >
            <Select
              className="w-full"
              options={[...DEPT_OPTIONS]}
              searchable
            />
          </Form.Item>
          <Form.Item
            name="assignedToUserId"
            label="Assignee"
            rules={[{ required: true, message: "Assignee is required" }]}
            getValueFromEvent={(v) => v}
          >
            <Select
              searchable
              className="w-full"
              options={userOptions}
              placeholder="Select assignee"
            />
          </Form.Item>
          <Form.Item
            name="isMandatory"
            valuePropName="checked"
            initialValue={true}
          >
            <Checkbox>Mandatory</Checkbox>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        centered
        transitionName=""
        maskTransitionName=""
        title="Waive task (HR)"
        open={waiveOpen}
        confirmLoading={isWaivingTask}
        onCancel={() => {
          setWaiveOpen(false);
          setWaiveTaskRow(null);
        }}
        onOk={() => void submitWaive()}
      >
        <Input.TextArea
          className={separationTextAreaClassName}
          rows={3}
          placeholder="Reason (required)"
          value={waiveReason}
          onChange={(e) => setWaiveReason(e.target.value)}
        />
      </Modal>

      <Modal
        centered
        transitionName=""
        maskTransitionName=""
        title="Complete task"
        open={Boolean(completeTaskRow)}
        confirmLoading={isCompletingTask}
        onCancel={() => {
          setCompleteTaskRow(null);
          setCompleteComments("");
        }}
        onOk={() => void submitCompleteTask()}
      >
        <p className="text-sm text-slate-600 mb-2">
          {completeTaskRow?.taskName}
        </p>
        <Input.TextArea
          className={separationTextAreaClassName}
          rows={2}
          placeholder="Comments (optional)"
          value={completeComments}
          onChange={(e) => setCompleteComments(e.target.value)}
        />
      </Modal>

      <Modal
        centered
        transitionName=""
        maskTransitionName=""
        title="Edit settlement"
        open={settlementOpen}
        width={720}
        confirmLoading={isSavingSettlement}
        onCancel={() => setSettlementOpen(false)}
        onOk={() => void submitSettlement()}
        destroyOnClose
        styles={{
          body: {
            maxHeight: "calc(100vh - 300px)",
            overflowY: "auto",
            overflowX: "hidden",
            paddingRight: "8px",
          },
        }}
      >
        <Form
          form={settlementForm}
          layout="vertical"
          requiredMark={separationFormRequiredMark}
        >
          <Form.Item name="notes" label="Notes">
            <Input.TextArea
              className={separationTextAreaClassName}
              rows={2}
            />
          </Form.Item>
          <Form.Item
            name="refreshLeaveBalances"
            valuePropName="checked"
            initialValue={false}
          >
            <Checkbox>Refresh leave balances (current year snapshot)</Checkbox>
          </Form.Item>
          <div className="mb-4">
            <Typography.Text strong className="block mb-3">
              Encashment lines
            </Typography.Text>
            <Form.List name="encashmentLines">
              {(fields, { add, remove }) => (
                <div className="space-y-4">
                  {fields.map((field) => (
                    <div
                      key={field.key}
                      className="flex flex-wrap items-end gap-3 p-3 bg-slate-50/50 rounded-lg border border-slate-100"
                    >
                      <Form.Item
                        {...field}
                        name={[field.name, "label"]}
                        label="Label"
                        className="mb-0 flex-1 min-w-[140px]"
                      >
                        <Input placeholder="e.g. Earned Leave" />
                      </Form.Item>
                      <Form.Item
                        {...field}
                        name={[field.name, "days"]}
                        label="Days"
                        className="mb-0 w-20"
                      >
                        <InputNumber min={0} className="w-full" />
                      </Form.Item>
                      <Form.Item
                        {...field}
                        name={[field.name, "ratePerDay"]}
                        label="Rate/day"
                        className="mb-0 w-24"
                      >
                        <InputNumber min={0} className="w-full" />
                      </Form.Item>
                      <Form.Item
                        {...field}
                        name={[field.name, "amount"]}
                        label="Amount"
                        className="mb-0 w-28"
                      >
                        <InputNumber min={0} className="w-full" />
                      </Form.Item>
                      <Button
                        type="text"
                        danger
                        className="flex items-center justify-center h-8 w-8 rounded-full hover:bg-red-50"
                        onClick={() => remove(field.name)}
                        icon={<Trash2 size={14} />}
                      />
                    </div>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    className="w-full h-10 border-slate-300 text-slate-500 hover:text-primary-600 hover:border-primary-500 flex items-center justify-center gap-2"
                  >
                    <Plus size={16} />
                    Add encashment line
                  </Button>
                </div>
              )}
            </Form.List>
          </div>

          <div className="mb-4">
            <Typography.Text strong className="block mb-3">
              Addition lines
            </Typography.Text>
            <Form.List name="additionLines">
              {(fields, { add, remove }) => (
                <div className="space-y-3">
                  {fields.map((field) => (
                    <div
                      key={field.key}
                      className="flex flex-wrap items-end gap-3 p-3 bg-slate-50/50 rounded-lg border border-slate-100"
                    >
                      <Form.Item
                        {...field}
                        name={[field.name, "label"]}
                        label="Label"
                        className="mb-0 flex-1 min-w-[140px]"
                      >
                        <Input placeholder="Description" />
                      </Form.Item>
                      <Form.Item
                        {...field}
                        name={[field.name, "amount"]}
                        label="Amount"
                        className="mb-0 w-32"
                      >
                        <InputNumber min={0} className="w-full" />
                      </Form.Item>
                      <Button
                        type="text"
                        danger
                        className="flex items-center justify-center h-8 w-8 rounded-full hover:bg-red-50"
                        onClick={() => remove(field.name)}
                        icon={<Trash2 size={14} />}
                      />
                    </div>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    className="w-full h-10 border-slate-300 text-slate-500 hover:text-primary-600 hover:border-primary-500 flex items-center justify-center gap-2"
                  >
                    <Plus size={16} />
                    Add addition line
                  </Button>
                </div>
              )}
            </Form.List>
          </div>

          <div className="mb-4">
            <Typography.Text strong className="block mb-3">
              Deduction lines
            </Typography.Text>
            <Form.List name="deductionLines">
              {(fields, { add, remove }) => (
                <div className="space-y-3">
                  {fields.map((field) => (
                    <div
                      key={field.key}
                      className="flex flex-wrap items-end gap-3 p-3 bg-slate-50/50 rounded-lg border border-slate-100"
                    >
                      <Form.Item
                        {...field}
                        name={[field.name, "label"]}
                        label="Label"
                        className="mb-0 flex-1 min-w-[140px]"
                      >
                        <Input placeholder="Description" />
                      </Form.Item>
                      <Form.Item
                        {...field}
                        name={[field.name, "amount"]}
                        label="Amount"
                        className="mb-0 w-32"
                      >
                        <InputNumber min={0} className="w-full" />
                      </Form.Item>
                      <Button
                        type="text"
                        danger
                        className="flex items-center justify-center h-8 w-8 rounded-full hover:bg-red-50"
                        onClick={() => remove(field.name)}
                        icon={<Trash2 size={14} />}
                      />
                    </div>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    className="w-full h-10 border-slate-300 text-slate-500 hover:text-primary-600 hover:border-primary-500 flex items-center justify-center gap-2"
                  >
                    <Plus size={16} />
                    Add deduction line
                  </Button>
                </div>
              )}
            </Form.List>
          </div>
        </Form>
      </Modal>

      <Modal
        title="Task Details"
        open={Boolean(viewTaskRow)}
        onCancel={() => setViewTaskRow(null)}
        footer={[
          <Button key="close" onClick={() => setViewTaskRow(null)}>
            Close
          </Button>,
        ]}
        centered
        transitionName=""
        maskTransitionName=""
        width={500}
      >
        {viewTaskRow && (
          <div className="space-y-4 py-2">
            <div>
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">
                Task Name
              </div>
              <div className="text-sm font-medium text-slate-800">
                {viewTaskRow.taskName}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">
                  Department
                </div>
                <div className="text-sm text-slate-700">
                  {viewTaskRow.department || "—"}
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">
                  Status
                </div>
                <SeparationStatusBadge
                  label={String(viewTaskRow.status ?? "—")}
                  phase={String(viewTaskRow.status ?? "")}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">
                  Assignee
                </div>
                <div className="text-sm text-slate-700">
                  {assigneeDisplay(viewTaskRow)}
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">
                  Mandatory
                </div>
                <div className="text-sm text-slate-700">
                  {viewTaskRow.isMandatory ? "Yes" : "No"}
                </div>
              </div>
            </div>

            {viewTaskRow.comments && (
              <div>
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">
                  Completion Comments
                </div>
                <div className="text-sm text-slate-600 bg-slate-50 p-2 rounded border border-slate-100">
                  {viewTaskRow.comments}
                </div>
              </div>
            )}

            {viewTaskRow.waivedReason && (
              <div>
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">
                  Waiver Reason
                </div>
                <div className="text-sm text-red-600 bg-red-50 p-2 rounded border border-red-100">
                  {viewTaskRow.waivedReason}
                </div>
              </div>
            )}

            {viewTaskRow.completedAt && (
              <div className="text-[10px] text-slate-400 pt-2 border-t border-slate-100 flex justify-between">
                {/* <span>Task ID: {viewTaskRow.id}</span> */}
                <span>
                  Completed: {new Date(viewTaskRow.completedAt).toLocaleString()}
                </span>
              </div>
            )}
          </div>
        )}
      </Modal>

      <ConfirmationModal
        isOpen={delTaskConfirmOpen}
        onClose={() => {
          setDelTaskConfirmOpen(false);
          setTaskToDelete(null);
        }}
        onConfirm={async () => {
          if (!taskToDelete || !exitId) return;
          try {
            await delTask({ exitId, taskId: taskToDelete.id }).unwrap();
            toast.success("Checklist task has been removed successfully");
            setDelTaskConfirmOpen(false);
            setTaskToDelete(null);
          } catch (e) {
            toast.error(getErrorMessage(e as never) ?? "Failed");
          }
        }}
        title="Remove task?"
        message={`Are you sure you want to remove "${taskToDelete?.taskName}"? This action cannot be undone.`}
        type="danger"
        confirmText="Remove task"
        isLoading={isDeletingTask}
      />

      <ConfirmationModal
        isOpen={submitInterviewConfirmOpen}
        onClose={() => setSubmitInterviewConfirmOpen(false)}
        onConfirm={async () => {
          if (!exitId) return;
          try {
            const responses = Object.entries(interviewDraft).map(
              ([questionKey, value]) => ({
                questionKey,
                value,
              }),
            );
            await patchResponses({ exitId, responses }).unwrap();
            await submitInterview(exitId).unwrap();
            toast.success("Exit interview has been submitted successfully");
            setSubmitInterviewConfirmOpen(false);
          } catch (e) {
            toast.error(getErrorMessage(e as never) ?? "The interview submission could not be completed");
          }
        }}
        title="Submit exit interview?"
        message="Once submitted, the interview will be locked and cannot be edited. Are you sure you want to proceed?"
        type="warning"
        confirmText="Submit interview"
        isLoading={isSubmittingInterview || isPatchingInterview}
      />

      <ConfirmationModal
        isOpen={completeSettlementConfirmOpen}
        onClose={() => setCompleteSettlementConfirmOpen(false)}
        onConfirm={async () => {
          if (!exitId) return;
          try {
            await completeSettlement(exitId).unwrap();
            await refetch();
            toast.success("Exit settlement has been completed successfully");
            setCompleteSettlementConfirmOpen(false);
          } catch (e) {
            toast.error(getErrorMessage(e as never) ?? "The settlement completion could not be finalized");
          }
        }}
        title="Mark settlement complete?"
        message="This will lock the settlement and cannot be undone. Are you sure you want to proceed?"
        type="warning"
        confirmText="Complete settlement"
        isLoading={isCompletingSettlement}
      />
    </div>
  );
}

export default function ExitLifecyclePage() {
  const { exitId } = useParams<{ exitId: string }>();
  if (!exitId) {
    return (
      <div className="p-4">
        <Typography.Text type="danger">Missing exit id in URL.</Typography.Text>
      </div>
    );
  }
  return (
    <ExitLifecycleContent
      exitId={exitId}
      variant="full"
      showHeaderLinks
    />
  );
}
